import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReactNode, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { canUseDashboard, signInUser, signInWithMicrosoft } from "../../firebase/firebase";
import { useSession } from "../../firebase/useSession";
import logo from "../../assets/images/logo.png";
import { Button } from "../../components/ui/CustomButton";
import { MicrosoftButton } from "../../components/ui/MicrosoftButton";
import { Spinner } from "../../components/ui/Spinner";
import { cn } from "../../utils/helper";
import EditPwDialog from "../dialogs/editPassword";

const signInScheme = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Please enter the email address you sign in with.")
    .email("That doesn't look like an email address. Please check it and try again."),
  password: z.string().min(1, "Please enter your password."),
});

type SignInScheme = z.infer<typeof signInScheme>;

const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary";

const fieldStyle = (invalid: boolean) => cn(
  "h-12 w-full rounded-lg border border-inputBorder bg-white px-3.5 text-base text-primaryText placeholder:text-placeholder",
  focusRing,
  invalid && "border-2 border-error px-[13px]",
);

const FieldError = ({ id, children }: { id: string; children: ReactNode }) => (
  <p id={id} role="alert" className="flex items-start gap-2 text-sm text-error">
    <AlertCircle aria-hidden="true" className="mt-0.5 h-[18px] w-[18px] shrink-0" />
    <span>{children}</span>
  </p>
);

const SignIn = () => {

  const [pwopen, setPwOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [msError, setMsError] = useState<string | null>(null);
  const [msSigningIn, setMsSigningIn] = useState(false);

  const handlePasswordResetClose = () => {
    setPwOpen(false);
  };

  const formData = useForm<SignInScheme>({
    mode: "onBlur",
    resolver: zodResolver(signInScheme),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const { register, formState: { errors, isSubmitting } } = formData;

  const navigate = useNavigate();
  const session = useSession();

  if (session.status === "signedIn" && canUseDashboard(session.user)) {
    return <Navigate to="/bookings" replace />;
  }

  const signInWithMicrosoftAccount = async () => {
    setMsError(null);
    setMsSigningIn(true);
    // Whatever's in the email field is passed on as a hint, so Microsoft can skip asking.
    const loginResponse = await signInWithMicrosoft(formData.getValues("email").trim());
    setMsSigningIn(false);
    if (loginResponse.user) {
      formData.reset();
      navigate("/bookings");
    } else {
      setMsError(loginResponse.error);
    }
  };

  return (
    <div className="flex w-full max-w-[400px] flex-col gap-6 min-[1080px]:gap-8">
      <img src={logo} alt="One International Towers" className="-ml-2 w-[104px] min-[1080px]:-ml-2.5 min-[1080px]:w-32" />

      <div className="flex flex-col gap-1 min-[1080px]:gap-2">
        <h1 className="text-2xl font-bold text-primaryText min-[1080px]:text-[28px] min-[1080px]:leading-9">Sign in</h1>
        <p className="text-base text-mutedText">Parking admin for One International Towers</p>
      </div>

      <form
        aria-label="Sign in"
        className="flex flex-col items-stretch gap-6"
        noValidate
        onSubmit={formData.handleSubmit(async (data) => {
          const loginResponse = await signInUser(data.email, data.password);
          if (loginResponse.error === null) {
            formData.reset();
            navigate("/bookings");
          } else {
            formData.setError(loginResponse.field, { type: "manual", message: loginResponse.error });
          }
        })}
      >
        <div className="flex flex-col gap-2">
          <label htmlFor="signin-email" className="text-sm font-semibold text-primaryText">Email</label>
          <input
            id="signin-email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "signin-email-error" : undefined}
            className={fieldStyle(!!errors.email)}
            {...register("email")}
          />
          {errors.email?.message && <FieldError id="signin-email-error">{errors.email.message}</FieldError>}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="signin-password" className="text-sm font-semibold text-primaryText">Password</label>
          <div className="relative">
            <input
              id="signin-password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? "signin-password-error" : undefined}
              className={cn(fieldStyle(!!errors.password), "pr-12")}
              {...register("password")}
            />
            <button
              type="button"
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              className={cn("absolute right-1 top-1 flex h-10 w-10 items-center justify-center rounded-md text-mutedText hover:text-primaryText", focusRing)}
              onClick={() => setShowPassword((shown) => !shown)}
            >
              {showPassword ? <EyeOff aria-hidden="true" className="h-5 w-5" /> : <Eye aria-hidden="true" className="h-5 w-5" />}
            </button>
          </div>
          {errors.password?.message && <FieldError id="signin-password-error">{errors.password.message}</FieldError>}
          <button
            type="button"
            className={cn("min-h-6 self-end rounded text-sm font-medium text-primary hover:text-primaryHover hover:underline", focusRing)}
            onClick={() => { setPwOpen(true); }}
          >
            Forgot password?
          </button>
        </div>

        <Button
          type="submit"
          className={cn("mt-1 w-full gap-3 rounded-lg text-base font-semibold transition-colors duration-150 hover:bg-primaryHover motion-reduce:transition-none", focusRing)}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Spinner fill="white" className="h-5 w-5 text-white/40 motion-reduce:animate-none" />
              Signing in…
            </>
          ) : "Sign in"}
        </Button>
      </form>

      <div className="flex items-center gap-4 text-sm text-mutedText">
        <div className="h-px flex-1 bg-divider" />
        <span>or</span>
        <div className="h-px flex-1 bg-divider" />
      </div>

      <div className="flex flex-col gap-2">
        <MicrosoftButton onClick={signInWithMicrosoftAccount} disabled={msSigningIn}>
          {msSigningIn ? "Waiting for Microsoft…" : "Sign in with Microsoft"}
        </MicrosoftButton>
        {msError && <FieldError id="signin-microsoft-error">{msError}</FieldError>}
      </div>

      <p className="text-sm text-mutedText">Need an account? Sign up in the OnePark mobile app first.</p>

      <EditPwDialog
        open={pwopen}
        onClose={handlePasswordResetClose}
        selectedValue={formData.getValues("email")}
        shouldDisable={false}
      />
    </div>
  );
};

export default SignIn;
